import React from 'react'
import classNames from 'classnames'

import { Checkbox } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'

import './CheckboxGroup.scss'

export const CheckboxGroup = ({
  label,
  field: { name, value },
  form: { touched, errors, setFieldValue, setFieldTouched },
  options,
  required,
  inline = true,
  ...props
}) => {
  const hasError = !!errors[name] && !!touched[name]

  // `value` is undefined for one render whenever the schema arrives after mount:
  // AbstractForm derives initialValues from the schema, but `enableReinitialize`
  // only syncs Formik in a post-render effect, so this component renders once
  // against values that predate its own field. IntegrationsSettings hits that on
  // every page load -- redux starts at `integration: {}`, so the schema is `[]`
  // until the fetch resolves. This is the only field component that dereferences
  // its value, so it is the only one that has to defend against it.
  const selected = Array.isArray(value) ? value : []

  const handleChange = (optionValue) => {
    setFieldTouched(name, true, false)
    if (selected.includes(optionValue)) {
      setFieldValue(
        name,
        selected.filter((item) => item !== optionValue)
      )
    } else {
      setFieldValue(name, [...selected, optionValue])
    }
  }

  return (
    <div
      className={classNames('checkbox-group', { 'checkbox-group--error': hasError })}
      data-testid={`checkboxGroup-${name}`}
    >
      <FormField required={required}>
        <label>{label}</label>
      </FormField>
      <div className={inline ? 'field-row compact' : 'field-stack'}>
        {options.map((option) => (
          <FormField key={option.value}>
            <Checkbox
              id={option.value}
              label={option.text || option.label}
              value={option.value}
              checked={selected.includes(option.value)}
              name={name}
              onChange={() => handleChange(option.value)}
              {...props}
            />
          </FormField>
        ))}
      </div>
      {hasError && <ErrorMessage content={errors[name]} />}
    </div>
  )
}
